<script lang="ts" setup>
import { fonts } from '~/composables/useFonts'

export type Fonts = {
    regular: string
    medium: string
    bold: string
}

await preloadComponents('PageHeader')

useNuxtApp().vueApp.config.warnHandler = () => '' // disable VUE warnings in browser console

// Cache-busting version of the shared cookie banner.
// Bump it whenever public/cookie-banner/ changes.
const COOKIE_BANNER_VERSION = '1.0.0'

const config = useRuntimeConfig()
const userTabbing = useUserTabbing()

const scrollLock = useState('scrollLock', () => false)

watchEffect(() => {
    if (import.meta.client) {
        // set scroll lock
        if (scrollLock.value) {
            document.body.classList.add('scroll-lock')
        } else {
            document.body.classList.remove('scroll-lock')
        }
    }
})

onMounted(() => {
    userTabbing.init()
})

onUnmounted(() => {
    userTabbing.destroy()
})

const metaTags: Record<string, string>[] = [
    {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1, viewport-fit=cover',
    },
]

if (config.public.AHREFS_SITE_VERIFICATION_ID) {
    metaTags.push({
        name: 'ahrefs-site-verification',
        content: config.public.AHREFS_SITE_VERIFICATION_ID,
    })
}

useHead({
    titleTemplate: `%s | ${config.public.seo.title}`,
    link: [
        {
            rel: 'preconnect',
            href: config.app.cdnURL,
        },
        {
            rel: 'preload',
            href: `${config.app.cdnURL}/fonts/${fonts.regular}`,
            as: 'font',
            type: 'font/woff2',
            crossorigin: '',
            tagPriority: 'critical',
        },
        {
            rel: 'preload',
            href: `${config.app.cdnURL}/fonts/${fonts.medium}`,
            as: 'font',
            type: 'font/woff2',
            crossorigin: '',
            tagPriority: 'critical',
        },
        {
            rel: 'preload',
            href: `${config.app.cdnURL}/fonts/${fonts.bold}`,
            as: 'font',
            type: 'font/woff2',
            crossorigin: '',
            tagPriority: 'critical',
        },
        {
            rel: 'stylesheet',
            href: '/dflip/css/dflip.css',
        },
        {
            rel: 'stylesheet',
            href: `${config.app.cdnURL}/cookie-banner/cookie-banner.css?v=${COOKIE_BANNER_VERSION}`,
        },
        ...useFavicons()
    ],
    meta: metaTags,
    htmlAttrs: {
        lang: 'sk',
    },
    style: [
        {
            innerHTML: useFonts(),
        },
    ],
    script: [
        {
            innerHTML: `
                const pRuntimeConfig = {
                    gtm_analytics_id: "${config.public.GTM_ANALYTICS_ID}",
                    gtm_marketing_id: "${config.public.GTM_MARKETING_ID}",
                    chat_cdn_url: "${config.app.cdnURL}",
                    images_url: "${config.public.PUBLIC_IMAGES_URL}",
                };
            `,
            type: 'text/javascript',
            tagPriority: 'low',
            tagPosition: 'bodyClose',
        },
        {
            // Per-site configuration of the shared cookie banner.
            // See public/cookie-banner/README.md for the supported keys.
            innerHTML: `
                window.KOOP_COOKIE_BANNER_CONFIG = {
                    cookieDomain: "${config.public.COOKIE_BANNER_DOMAIN}",
                    policyUrl: "/cookies",
                };
            `,
            type: 'text/javascript',
            tagPriority: 'low',
            tagPosition: 'bodyClose',
        },
        {
            src: `${config.app.cdnURL}/cookie-banner/cookie-banner.js?v=${COOKIE_BANNER_VERSION}`,
            defer: true,
            fetchpriority: 'low',
            tagPriority: 'low',
            tagPosition: 'bodyClose',
        },
        // marketing gtm
        {
            src: `${config.app.cdnURL}/js/gtm-marketing.js?v=202609221`,
            defer: true,
            fetchpriority: 'low',
            tagPriority: 'low',
            tagPosition: 'bodyClose',
        },
        {
            src: '/dflip/js/libs/jquery.min.js',
            defer: true,
            fetchpriority: 'high',
            tagPriority: 'low',
            tagPosition: 'head',
        },

        {
            src: '/dflip/js/dflip.js',
            defer: true,
            fetchpriority: 'low',
            tagPriority: 'low',
            tagPosition: 'head',
        },
    ],
})

const pdfSource = usePdfPreview()
</script>
<template>
  <NuxtErrorBoundary>
    <NuxtLayout>
      <NuxtPage />

      <ClientOnly>
        <ModalPdfPreview
          v-if="pdfSource"
          :source="pdfSource"
          @close="pdfSource = undefined"
        />
      </ClientOnly>
    </NuxtLayout>
  </NuxtErrorBoundary>
</template>
